import { Link, useNavigate } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";
import { EllipsisVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "@/components/animate-ui/icons/chevron-down";
import { ClipboardList } from "@/components/animate-ui/icons/clipboard-list";
import { MessageCircleWarning } from "@/components/animate-ui/icons/message-circle-warning";
import { PanelTopOpen } from "@/components/animate-ui/icons/panel-top-open";
import { User } from "@/components/animate-ui/icons/user";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useArchiveRun, useRunListFreshness, useUserRuns } from "@/lib/api/hooks/tasks";
import type { components } from "@/lib/api/types";
import {
	channelIcons,
	channelLabel,
	getStatusColor,
	getStatusLabel,
	isActivelyRunning,
	isAwaitingInput,
} from "@/lib/utils/run-meta";
import { useTeammateUIStore } from "@/stores/teammate-ui-store";

type RunResponse = components["schemas"]["RunResponse"];

const getStatusIcon = (run: RunResponse) => {
	if (isAwaitingInput(run))
		return <MessageCircleWarning className="size-4 shrink-0 text-amber-500 animate-pulse" />;
	if (isActivelyRunning(run))
		return <Spinner className="size-3.5 shrink-0 text-foreground [animation-duration:1.5s]" />;
	return null;
};

export function NavHistory() {
	const [isOpen, setIsOpen] = useState(true);
	const scrollRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const setSelectedTask = useTeammateUIStore((s) => s.setSelectedTask);

	// Archive confirmation dialog state
	const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
	const [runToArchive, setRunToArchive] = useState<number | null>(null);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useUserRuns(20);
	const archiveRun = useArchiveRun();
	useRunListFreshness();

	// Flatten all pages into single array
	const runs = data?.pages.flat() ?? [];

	// Intersection observer for infinite scroll
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const sentinel = entries[0];
				if (sentinel?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{
				root: scrollRef.current,
				rootMargin: "100px",
				threshold: 0.1,
			}
		);

		if (sentinelRef.current) {
			observer.observe(sentinelRef.current);
		}

		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Hide section while loading and when empty — avoids flash of empty state
	if (isLoading || runs.length === 0) {
		return null;
	}

	return (
		<SidebarGroup className="pb-2 pt-2">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				{/* Fully clickable header */}
				<CollapsibleTrigger className="flex items-center gap-2 px-2 py-1 w-full hover:bg-sidebar-accent rounded-md transition-colors group">
					<span className="text-sm font-medium truncate flex-1 min-w-0 text-left text-sidebar-foreground">
						Runs
					</span>
					<ChevronDown
						animateOnHover
						className={`size-4 shrink-0 transition-transform ml-auto ${
							isOpen ? "rotate-0" : "-rotate-90"
						}`}
					/>
				</CollapsibleTrigger>

				<CollapsibleContent>
					<SidebarGroupContent>
						<ScrollArea
							ref={scrollRef}
							className="max-h-[250px] [&_[data-slot=scroll-area-viewport]]:h-auto [&_[data-slot=scroll-area-viewport]]:max-h-[250px]"
						>
							<SidebarMenu>
								{runs.map((run) => (
									<SidebarMenuItem key={run.id}>
										<SidebarMenuButton asChild className="py-1 [&>span:last-child]:truncate">
											<Link to="/runs/$runId" params={{ runId: String(run.id) }}>
												{(() => {
													const icon = getStatusIcon(run);
													const label = getStatusLabel(run);
													if (!icon || !label) return null;
													return (
														<Tooltip delayDuration={300}>
															<TooltipTrigger asChild>{icon}</TooltipTrigger>
															<TooltipContent variant="subtle" side="right">
																{label}
															</TooltipContent>
														</Tooltip>
													);
												})()}
												<span className="flex flex-col flex-1 w-0 overflow-hidden">
													<span className="text-xs font-medium truncate">
														{run.description || "Untitled run"}
													</span>
													<span className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
														{(() => {
															const color = getStatusColor(run);
															const label = getStatusLabel(run);
															if (!color) return null;
															return (
																<Tooltip delayDuration={300}>
																	<TooltipTrigger asChild>
																		<span className={`size-1.5 rounded-full shrink-0 ${color}`} />
																	</TooltipTrigger>
																	<TooltipContent variant="subtle" side="right">
																		{label}
																	</TooltipContent>
																</Tooltip>
															);
														})()}
														{run.created_at ? (
															<Tooltip delayDuration={300}>
																<TooltipTrigger asChild>
																	<span>
																		{formatDistanceToNow(new Date(run.created_at), {
																			addSuffix: true,
																		}).replace(/^about /, "")}
																	</span>
																</TooltipTrigger>
																<TooltipContent variant="subtle" side="right">
																	{format(new Date(run.created_at), "MMM d, yyyy 'at' h:mm a")}
																</TooltipContent>
															</Tooltip>
														) : null}
														{(() => {
															const Icon = channelIcons[run.channel];
															const chLabel = channelLabel[run.channel];
															if (!Icon || !chLabel) return null;
															return (
																<Tooltip delayDuration={300}>
																	<TooltipTrigger asChild>
																		<Icon className="size-2 shrink-0" />
																	</TooltipTrigger>
																	<TooltipContent variant="subtle" side="right">
																		{chLabel}
																	</TooltipContent>
																</Tooltip>
															);
														})()}
													</span>
												</span>
											</Link>
										</SidebarMenuButton>

										{/* 3-dot menu */}
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<SidebarMenuAction showOnHover>
													<EllipsisVertical className="size-4" />
													<span className="sr-only">Run options</span>
												</SidebarMenuAction>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem asChild>
													<Link to="/teammates/$id" params={{ id: String(run.instance_id) }}>
														<User className="size-4" animateOnHover />
														See mate
													</Link>
												</DropdownMenuItem>
												{run.task_id != null && (
													<DropdownMenuItem
														onClick={() => {
															setSelectedTask(run.task_id as number);
															navigate({
																to: "/teammates/$id",
																params: { id: String(run.instance_id) },
															});
														}}
													>
														<ClipboardList className="size-4" animateOnHover />
														Open task
													</DropdownMenuItem>
												)}
												<DropdownMenuItem
													onClick={(e) => {
														e.preventDefault();
														setRunToArchive(run.id);
														setIsArchiveDialogOpen(true);
													}}
												>
													<PanelTopOpen className="size-4" />
													Archive
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</SidebarMenuItem>
								))}

								{/* Sentinel for infinite scroll */}
								<div ref={sentinelRef} className="h-4" />

								{/* Loading indicator */}
								{isFetchingNextPage && (
									<div className="px-3 py-1.5 text-sm text-muted-foreground text-center">
										Loading more...
									</div>
								)}
							</SidebarMenu>
						</ScrollArea>
					</SidebarGroupContent>
				</CollapsibleContent>
			</Collapsible>

			{/* Archive Confirmation Dialog */}
			<Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Archive Run?</DialogTitle>
						<DialogDescription>
							This will archive the run. You can view archived in run history of the task.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsArchiveDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={() => {
								if (runToArchive !== null) {
									archiveRun.mutate(runToArchive);
									setIsArchiveDialogOpen(false);
									setRunToArchive(null);
								}
							}}
						>
							Archive
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SidebarGroup>
	);
}
